import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import ContactForm from "./components/ContactForm";
import AiChatWidget from "./components/AiChatWidget";
import LoadingState from "@/components/base/LoadingState";
import ErrorState from "@/components/base/ErrorState";

/* ─── Types ──────────────────────────────────────────────────── */
type Tenant = {
  id: string;
  name: string;
  slug: string;
  status: string;
  plan_code: string;
  custom_domain: string | null;
  custom_domain_enabled: boolean;
  custom_domain_status: string | null;
};

type Branding = {
  business_name: string;
  logo_url: string | null;
  primary_color: string;
  secondary_color: string;
  phone: string | null;
  email: string | null;
  website: string | null;
  address: string | null;
};

type GalleryItem = {
  id: string;
  title: string | null;
  image_url: string;
  category: string | null;
  sort_order: number;
};

type ServiceItem = {
  id: string;
  title: string;
  description: string | null;
  price_from: number | null;
  image_url: string | null;
  sort_order: number;
};

type AiWidgetData = {
  assistantName: string;
  show: boolean;
};

type WebsiteContent = {
  hero_title:      string | null;
  hero_subtitle:   string | null;
  about_title:     string | null;
  about_text:      string | null;
  services_title:  string | null;
  gallery_title:   string | null;
  contact_title:   string | null;
  is_published:    boolean;
  seo_title:       string | null;
  seo_description: string | null;
  seo_keywords:    string | null;
  og_image:        string | null;
};

type PageStatus = "loading" | "active" | "not_found" | "unavailable" | "unpublished" | "error";

interface TenantPublicPageProps {
  preResolvedTenantId?: string;
  preResolvedSlug?: string;
}

/* ─── Content defaults ────────────────────────────────────────── */
const CONTENT_DEFAULTS = {
  hero_title:     "Capturing Moments That Last Forever",
  hero_subtitle:  "Professional wedding photography and cinematic films crafted with passion, artistry, and attention to every detail.",
  about_title:    "Storytelling Through the Lens",
  about_text:     "We believe every love story deserves to be told with authenticity and artistry. Our approach blends documentary candor with editorial elegance, creating images that feel both timeless and deeply personal.",
  services_title: "Our Services",
  gallery_title:  "Recent Work",
  contact_title:  "Let\u2019s Create Something Beautiful",
};

/* ─── Empty state sub-components ─────────────────────────────── */
function ServicesEmptyState({ primary }: { primary: string }) {
  return (
    <div className="col-span-full py-16 flex flex-col items-center gap-3">
      <div className="w-14 h-14 flex items-center justify-center rounded-2xl" style={{ backgroundColor: primary + "12" }}>
        <i className="ri-camera-3-line text-2xl" style={{ color: primary + "80" }}></i>
      </div>
      <p className="text-[#1A1A2E]/40 text-sm text-center max-w-xs">
        Services will appear here once configured by the studio.
      </p>
    </div>
  );
}

function GalleryEmptyState({ primary }: { primary: string }) {
  return (
    <div className="col-span-full py-16 flex flex-col items-center gap-3">
      <div className="w-14 h-14 flex items-center justify-center rounded-2xl" style={{ backgroundColor: primary + "12" }}>
        <i className="ri-image-line text-2xl" style={{ color: primary + "80" }}></i>
      </div>
      <p className="text-[#1A1A2E]/40 text-sm text-center max-w-xs">
        Portfolio photos will appear here once the studio adds them.
      </p>
    </div>
  );
}

/* ─── Component ──────────────────────────────────────────────── */
export default function TenantPublicPage({ preResolvedTenantId, preResolvedSlug }: TenantPublicPageProps) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [activeGalleryFilter, setActiveGalleryFilter] = useState<string | null>(null);
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const [tenant, setTenant]   = useState<Tenant | null>(null);
  const [branding, setBranding] = useState<Branding | null>(null);
  const [content, setContent] = useState<WebsiteContent | null>(null);
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [gallery, setGallery]   = useState<GalleryItem[]>([]);
  const [aiWidget, setAiWidget] = useState<AiWidgetData | null>(null);
  const [pageStatus, setPageStatus] = useState<PageStatus>("loading");

  /* Effective slug — from props (resolver) or URL param */
  const effectiveSlug = preResolvedSlug ?? tenantSlug ?? "";

  useEffect(() => {
    const fetchData = async () => {
      try {
        let tData: Tenant | null = null;

        if (preResolvedTenantId) {
          /* Resolver already found tenant by hostname — fetch full record */
          const { data } = await supabase
            .from("saas_tenants")
            .select("id, name, slug, status, plan_code, custom_domain, custom_domain_enabled, custom_domain_status")
            .eq("id", preResolvedTenantId)
            .maybeSingle();
          if (data) tData = data as Tenant;
        } else if (tenantSlug) {
          /* Normal slug-based lookup */
          const { data } = await supabase
            .from("saas_tenants")
            .select("id, name, slug, status, plan_code, custom_domain, custom_domain_enabled, custom_domain_status")
            .eq("slug", tenantSlug)
            .maybeSingle();
          if (data) tData = data as Tenant;
        }

        if (!tData) { setPageStatus("not_found"); return; }
        if (tData.status !== "active") { setPageStatus("unavailable"); return; }

        setTenant(tData);

        /* ── 2. Parallel load of all data ── */
        const tenantId = tData.id;
        const [
          brandingRes,
          contentRes,
          servicesRes,
          galleryRes,
          aiSettingsRes,
          aiModuleRes,
        ] = await Promise.all([
          supabase
            .from("saas_tenant_branding")
            .select("business_name, logo_url, primary_color, secondary_color, phone, email, website, address")
            .eq("tenant_id", tenantId)
            .maybeSingle(),
          supabase
            .from("tenant_website_content")
            .select("hero_title, hero_subtitle, about_title, about_text, services_title, gallery_title, contact_title, is_published, seo_title, seo_description, seo_keywords, og_image")
            .eq("tenant_id", tenantId)
            .maybeSingle(),
          supabase
            .from("tenant_services")
            .select("id, title, description, price_from, image_url, sort_order")
            .eq("tenant_id", tenantId)
            .eq("is_active", true)
            .order("sort_order", { ascending: true })
            .order("created_at", { ascending: true }),
          supabase
            .from("tenant_gallery_items")
            .select("id, title, image_url, category, sort_order")
            .eq("tenant_id", tenantId)
            .eq("is_active", true)
            .order("sort_order", { ascending: true })
            .order("created_at", { ascending: true }),
          supabase
            .from("tenant_ai_settings")
            .select("assistant_enabled, assistant_name")
            .eq("tenant_id", tenantId)
            .maybeSingle(),
          supabase
            .from("tenant_modules")
            .select("is_enabled")
            .eq("tenant_id", tenantId)
            .eq("module_id", "ai_sales_assistant")
            .eq("is_enabled", true)
            .maybeSingle(),
        ]);

        /* ── 3. Check published status ── */
        const c = contentRes.data as WebsiteContent | null;
        if (c && c.is_published === false) {
          setPageStatus("unpublished");
          return;
        }

        setBranding(brandingRes.data as Branding | null);
        setContent(c);
        setServices((servicesRes.data as ServiceItem[]) ?? []);
        setGallery((galleryRes.data as GalleryItem[]) ?? []);

        /* ── 4. AI Widget ── */
        const aiS = aiSettingsRes.data as { assistant_enabled: boolean; assistant_name: string } | null;
        if (!!aiModuleRes.data && aiS?.assistant_enabled) {
          setAiWidget({ assistantName: aiS.assistant_name || "Sales Assistant", show: true });
        }

        setPageStatus("active");
      } catch {
        setPageStatus("error");
      }
    };

    fetchData();
  }, [tenantSlug, preResolvedTenantId]);

  /* ── Loading ── */
  if (pageStatus === "loading") {
    return <LoadingState fullPage theme="light" text="Loading studio page…" />;
  }

  /* ── Network error ── */
  if (pageStatus === "error") {
    return (
      <ErrorState
        fullPage
        theme="light"
        title="Couldn't load this page"
        message="There was a connection error. Please check your internet and try again."
        onRetry={() => window.location.reload()}
      />
    );
  }

  /* ── Tenant not found ── */
  if (pageStatus === "not_found") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-slate-100 mx-auto mb-5">
            <i className="ri-store-line text-slate-400 text-3xl"></i>
          </div>
          <h1 className="text-[#1A1A2E] text-xl font-semibold mb-2">Studio Not Found</h1>
          <p className="text-[#1A1A2E]/50 text-sm mb-6">
            This studio page does not exist. Please check the URL and try again.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Pixory AI
          </Link>
        </div>
      </div>
    );
  }

  /* ── Tenant exists but inactive / unpublished ── */
  if (pageStatus === "unavailable" || pageStatus === "unpublished") {
    const isUnpublished = pageStatus === "unpublished";
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-amber-50 mx-auto mb-5">
            <i className="ri-time-line text-amber-400 text-3xl"></i>
          </div>
          <h1 className="text-[#1A1A2E] text-xl font-semibold mb-2">
            {isUnpublished ? "Page Not Published Yet" : "Studio Temporarily Unavailable"}
          </h1>
          <p className="text-[#1A1A2E]/50 text-sm mb-6">
            {isUnpublished
              ? "This studio is still setting up their page. Please check back soon."
              : "This studio page is temporarily unavailable. Please try again later or contact the studio directly."}
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Pixory AI
          </Link>
        </div>
      </div>
    );
  }

  /* ── Resolved values ── */
  const primary   = branding?.primary_color   ?? "#C8A96E";
  const secondary = branding?.secondary_color ?? "#1A1A2E";
  const bizName   = branding?.business_name   ?? tenant!.name;
  const logoUrl   = branding?.logo_url        ?? null;
  const phone     = branding?.phone           ?? null;
  const email     = branding?.email           ?? null;
  const website   = branding?.website         ?? null;
  const address   = branding?.address         ?? null;

  const heroTitle     = content?.hero_title     || CONTENT_DEFAULTS.hero_title;
  const heroSubtitle  = content?.hero_subtitle  || CONTENT_DEFAULTS.hero_subtitle;
  const aboutTitle    = content?.about_title    || CONTENT_DEFAULTS.about_title;
  const aboutText     = content?.about_text     || CONTENT_DEFAULTS.about_text;
  const servicesTitle = content?.services_title || CONTENT_DEFAULTS.services_title;
  const galleryTitle  = content?.gallery_title  || CONTENT_DEFAULTS.gallery_title;
  const contactTitle  = content?.contact_title  || CONTENT_DEFAULTS.contact_title;

  const seoTitle       = content?.seo_title?.trim()       || `${bizName} | Photography & Video Services`;
  const seoDescription = content?.seo_description?.trim() || `${bizName} \u2014 professional photography studio. Weddings, portraits, events and more.`;
  const seoKeywords    = content?.seo_keywords?.trim()    || null;
  const ogImageUrl     = content?.og_image?.trim()        || null;

  const hasActiveDomain =
    tenant!.custom_domain_enabled === true &&
    tenant!.custom_domain_status === "active" &&
    !!tenant!.custom_domain;

  /* ── Gallery categories ── */
  const galleryCategories = [...new Set(gallery.map((g) => g.category).filter(Boolean) as string[])];
  const filteredGallery   = activeGalleryFilter
    ? gallery.filter((g) => g.category === activeGalleryFilter)
    : gallery;

  return (
    <div className="min-h-screen bg-white">
      {/* ── SEO meta tags ── */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      {seoKeywords && <meta name="keywords" content={seoKeywords} />}
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:type" content="website" />
      {ogImageUrl && (
        <>
          <meta property="og:image" content={ogImageUrl} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
        </>
      )}
      {hasActiveDomain && (
        <meta property="og:url" content={`https://${tenant!.custom_domain}`} />
      )}
      <meta name="twitter:card" content="summary_large_image" />
      {ogImageUrl && <meta name="twitter:image" content={ogImageUrl} />}
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />

      {/* ── Navbar ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-black/5">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img src={logoUrl} alt={bizName} className="h-8 w-auto object-contain" />
            ) : (
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                style={{ backgroundColor: primary }}
              >
                <i className="ri-camera-line text-white text-sm"></i>
              </div>
            )}
            <span className="text-[#1A1A2E] font-semibold text-sm">{bizName}</span>
          </div>
          <div className="hidden md:flex items-center gap-6">
            {["Services", "Gallery", "About", "Contact"].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                className="text-[#1A1A2E]/60 hover:text-[#1A1A2E] text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                {item}
              </a>
            ))}
          </div>
          <a
            href="#contact"
            className="hidden md:flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: primary }}
          >
            <i className="ri-calendar-check-line"></i>
            Book Now
          </a>
          <button
            onClick={() => setMobileNavOpen(!mobileNavOpen)}
            className="md:hidden w-9 h-9 flex items-center justify-center text-[#1A1A2E]/60 hover:text-[#1A1A2E] transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={mobileNavOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
          </button>
        </div>
        {mobileNavOpen && (
          <div className="md:hidden border-t border-black/5 bg-white/95 backdrop-blur-md px-4 py-3 space-y-1">
            {["Services", "Gallery", "About", "Contact"].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                onClick={() => setMobileNavOpen(false)}
                className="block text-[#1A1A2E]/70 hover:text-[#1A1A2E] text-sm font-medium py-2.5 border-b border-black/5 last:border-0 cursor-pointer"
              >
                {item}
              </a>
            ))}
            <div className="pt-2">
              <a
                href="#contact"
                onClick={() => setMobileNavOpen(false)}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-sm font-semibold text-white cursor-pointer"
                style={{ backgroundColor: primary }}
              >
                <i className="ri-calendar-check-line"></i>
                Book Now
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* ── Hero ── */}
      <section className="relative pt-16">
        <div className="relative h-[520px] md:h-[640px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20romantic%20wedding%20photography%20scene%20with%20soft%20golden%20hour%20lighting%2C%20bride%20and%20groom%20silhouette%20against%20warm%20sunset%2C%20elegant%20and%20cinematic%20composition%20with%20shallow%20depth%20of%20field%2C%20professional%20photography%20style%2C%20warm%20amber%20and%20cream%20tones%2C%20artistic%20and%20timeless%20mood%2C%20high-end%20editorial%20quality&width=1440&height=640&seq=tenant-hero-bg-1&orientation=landscape"
            alt={`${bizName} \u2014 wedding photography`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center max-w-2xl px-6 w-full">
              <p className="text-white/60 text-xs uppercase tracking-[0.2em] mb-4">{bizName}</p>
              <h1 className="text-white text-3xl md:text-5xl font-bold leading-tight mb-5">
                {heroTitle}
              </h1>
              <p className="text-white/60 text-sm md:text-base max-w-lg mx-auto mb-8 leading-relaxed">
                {heroSubtitle}
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <a
                  href="#contact"
                  className="flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold text-white transition-colors cursor-pointer whitespace-nowrap"
                  style={{ backgroundColor: primary }}
                >
                  <i className="ri-calendar-check-line"></i>
                  Book a Session
                </a>
                <a
                  href="#gallery"
                  className="flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold text-white border border-white/30 hover:border-white/50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-circle-line"></i>
                  View Portfolio
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Services ── */}
      <section id="services" className="py-20 md:py-28 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: primary }}>
              What We Offer
            </p>
            <h2 className="text-[#1A1A2E] text-2xl md:text-3xl font-bold mb-3">
              {servicesTitle}
            </h2>
            <p className="text-[#1A1A2E]/50 text-sm max-w-md mx-auto">
              From intimate elopements to grand celebrations, we cover every moment.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.length === 0 ? (
              <ServicesEmptyState primary={primary} />
            ) : (
              services.map((svc) => (
                <div
                  key={svc.id}
                  className="group rounded-xl border border-black/5 p-6 hover:border-black/10 transition-all"
                  style={{ backgroundColor: "#fafafa" }}
                >
                  {svc.image_url ? (
                    <div className="w-full h-36 rounded-xl overflow-hidden mb-4">
                      <img
                        src={svc.image_url}
                        alt={svc.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  ) : (
                    <div
                      className="w-12 h-12 flex items-center justify-center rounded-xl mb-4"
                      style={{ backgroundColor: primary + "18" }}
                    >
                      <i className="ri-image-line text-xl" style={{ color: primary }}></i>
                    </div>
                  )}
                  <h3 className="text-[#1A1A2E] font-semibold text-sm mb-2">{svc.title}</h3>
                  {svc.description && (
                    <p className="text-[#1A1A2E]/50 text-xs leading-relaxed mb-3">{svc.description}</p>
                  )}
                  {svc.price_from != null ? (
                    <p className="text-xs font-semibold" style={{ color: primary }}>
                      From ${Number(svc.price_from).toLocaleString()}
                    </p>
                  ) : (
                    <p className="text-[#1A1A2E]/30 text-xs italic">Ask for pricing</p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* ── Gallery ── */}
      <section id="gallery" className="py-20 md:py-28 px-6" style={{ backgroundColor: secondary + "08" }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: primary }}>
              Portfolio
            </p>
            <h2 className="text-[#1A1A2E] text-2xl md:text-3xl font-bold mb-3">
              {galleryTitle}
            </h2>
            <p className="text-[#1A1A2E]/50 text-sm max-w-md mx-auto">
              A glimpse into the stories we have had the honor to capture.
            </p>
          </div>

          {/* Category filter */}
          {galleryCategories.length > 0 && (
            <div className="flex flex-wrap gap-2 justify-center mb-8">
              <button
                onClick={() => setActiveGalleryFilter(null)}
                className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
                  activeGalleryFilter === null
                    ? "text-white border-transparent"
                    : "border-black/10 text-[#1A1A2E]/50 bg-white hover:border-black/20"
                }`}
                style={activeGalleryFilter === null ? { backgroundColor: primary, borderColor: primary } : {}}
              >
                All
              </button>
              {galleryCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveGalleryFilter(cat)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
                    activeGalleryFilter === cat
                      ? "text-white border-transparent"
                      : "border-black/10 text-[#1A1A2E]/50 bg-white hover:border-black/20"
                  }`}
                  style={activeGalleryFilter === cat ? { backgroundColor: primary, borderColor: primary } : {}}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {gallery.length === 0 ? (
            <div className="grid grid-cols-1">
              <GalleryEmptyState primary={primary} />
            </div>
          ) : filteredGallery.length === 0 ? (
            <div className="py-12 flex flex-col items-center gap-3">
              <i className="ri-filter-off-line text-2xl text-[#1A1A2E]/20"></i>
              <p className="text-[#1A1A2E]/40 text-sm">No photos in this category.</p>
              <button
                onClick={() => setActiveGalleryFilter(null)}
                className="text-xs font-medium cursor-pointer transition-colors"
                style={{ color: primary }}
              >
                Show all photos
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {filteredGallery.map((item, i) => (
                <div key={item.id} className="relative aspect-[4/5] rounded-xl overflow-hidden group cursor-pointer">
                  <img
                    src={item.image_url}
                    alt={item.title ?? `Gallery ${i + 1}`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-colors flex flex-col items-center justify-center gap-1 p-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                      <i className="ri-zoom-in-line text-lg"></i>
                    </div>
                    {item.title && (
                      <p className="text-white text-xs font-medium text-center opacity-0 group-hover:opacity-100 transition-opacity mt-1 line-clamp-2">
                        {item.title}
                      </p>
                    )}
                    {item.category && (
                      <span className="text-white/60 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">
                        {item.category}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── About ── */}
      <section id="about" className="py-20 md:py-28 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div className="relative">
              <div className="rounded-2xl overflow-hidden aspect-[4/5]">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20wedding%20photographer%20at%20work%20during%20outdoor%20ceremony%2C%20holding%20camera%20with%20telephoto%20lens%2C%20candid%20behind-the-scenes%20moment%2C%20natural%20daylight%2C%20warm%20and%20authentic%20documentary%20style%20photography%2C%20professional%20camera%20equipment%20visible%2C%20beautiful%20garden%20setting&width=500&height=625&seq=tenant-about-photo-1&orientation=portrait"
                  alt={`About ${bizName}`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div
                className="absolute -bottom-4 right-0 md:-right-4 rounded-xl p-4 border"
                style={{ backgroundColor: primary + "12", borderColor: primary + "30" }}
              >
                <p className="text-2xl font-bold" style={{ color: primary }}>10+</p>
                <p className="text-[#1A1A2E]/50 text-xs">Years of Experience</p>
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: primary }}>
                About {bizName}
              </p>
              <h2 className="text-[#1A1A2E] text-2xl md:text-3xl font-bold mb-4">
                {aboutTitle}
              </h2>
              <p className="text-[#1A1A2E]/60 text-sm leading-relaxed mb-6 whitespace-pre-line">
                {aboutText}
              </p>
              <div className="flex items-center gap-4 md:gap-6 flex-wrap">
                <div>
                  <p className="text-xl font-bold text-[#1A1A2E]">500+</p>
                  <p className="text-[#1A1A2E]/40 text-xs">Weddings Captured</p>
                </div>
                <div className="w-px h-8 bg-black/10"></div>
                <div>
                  <p className="text-xl font-bold text-[#1A1A2E]">50+</p>
                  <p className="text-[#1A1A2E]/40 text-xs">Awards Won</p>
                </div>
                <div className="w-px h-8 bg-black/10"></div>
                <div>
                  <p className="text-xl font-bold text-[#1A1A2E]">100%</p>
                  <p className="text-[#1A1A2E]/40 text-xs">Happy Clients</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Contact ── */}
      <section id="contact" className="py-20 md:py-28 px-6" style={{ backgroundColor: secondary + "08" }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: primary }}>
                Get in Touch
              </p>
              <h2 className="text-[#1A1A2E] text-2xl md:text-3xl font-bold mb-4">
                {contactTitle}
              </h2>
              <p className="text-[#1A1A2E]/50 text-sm leading-relaxed mb-8">
                Ready to tell your story? Reach out and we will discuss how we can capture
                your most precious moments with the artistry they deserve.
              </p>
              <div className="space-y-4">
                {phone && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg" style={{ backgroundColor: primary + "15" }}>
                      <i className="ri-phone-line" style={{ color: primary }}></i>
                    </div>
                    <div>
                      <p className="text-[#1A1A2E]/40 text-xs">Phone</p>
                      <p className="text-[#1A1A2E] text-sm font-medium">{phone}</p>
                    </div>
                  </div>
                )}
                {email && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg" style={{ backgroundColor: primary + "15" }}>
                      <i className="ri-mail-line" style={{ color: primary }}></i>
                    </div>
                    <div>
                      <p className="text-[#1A1A2E]/40 text-xs">Email</p>
                      <a href={`mailto:${email}`} className="text-[#1A1A2E] text-sm font-medium hover:underline">
                        {email}
                      </a>
                    </div>
                  </div>
                )}
                {address && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg" style={{ backgroundColor: primary + "15" }}>
                      <i className="ri-map-pin-line" style={{ color: primary }}></i>
                    </div>
                    <div>
                      <p className="text-[#1A1A2E]/40 text-xs">Address</p>
                      <p className="text-[#1A1A2E] text-sm font-medium">{address}</p>
                    </div>
                  </div>
                )}
                {website && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg" style={{ backgroundColor: primary + "15" }}>
                      <i className="ri-global-line" style={{ color: primary }}></i>
                    </div>
                    <div>
                      <p className="text-[#1A1A2E]/40 text-xs">Website</p>
                      <a
                        href={website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium hover:underline"
                        style={{ color: primary }}
                      >
                        {website.replace(/^https?:\/\//, "")}
                      </a>
                    </div>
                  </div>
                )}
                {!phone && !email && !address && !website && (
                  <p className="text-[#1A1A2E]/30 text-sm italic">
                    Contact details will appear here once configured by the studio.
                  </p>
                )}
              </div>
            </div>
            <ContactForm tenantId={tenant!.id} primary={primary} />
          </div>
        </div>
      </section>

      {/* ── AI Chat Widget ── */}
      {aiWidget?.show && (
        <AiChatWidget
          tenantId={tenant!.id}
          assistantName={aiWidget.assistantName}
          primary={primary}
        />
      )}

      {/* ── Footer ── */}
      <footer className="border-t border-black/5 py-10 px-6" style={{ backgroundColor: "#f8f6f3" }}>
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {logoUrl ? (
                <img src={logoUrl} alt={bizName} className="h-6 w-auto object-contain" />
              ) : (
                <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ backgroundColor: primary }}>
                  <i className="ri-camera-line text-white text-xs"></i>
                </div>
              )}
              <span className="text-[#1A1A2E] font-semibold text-sm">{bizName}</span>
            </div>
            <p className="text-[#1A1A2E]/30 text-xs">
              &copy; {new Date().getFullYear()} {bizName}. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              {email   && <a href={`mailto:${email}`} className="text-[#1A1A2E]/30 hover:text-[#1A1A2E]/60 transition-colors cursor-pointer"><i className="ri-mail-line"></i></a>}
              {phone   && <a href={`tel:${phone}`}    className="text-[#1A1A2E]/30 hover:text-[#1A1A2E]/60 transition-colors cursor-pointer"><i className="ri-phone-line"></i></a>}
              {website && <a href={website} target="_blank" rel="noopener noreferrer" className="text-[#1A1A2E]/30 hover:text-[#1A1A2E]/60 transition-colors cursor-pointer"><i className="ri-global-line"></i></a>}
            </div>
          </div>

          {/* Custom domain indicator */}
          {hasActiveDomain && (
            <div className="mt-6 pt-4 border-t border-black/8 flex items-center justify-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                <i className="ri-global-line text-xs"></i>
              </span>
              <p className="text-emerald-700 text-xs font-medium">
                Custom domain active: <span className="font-mono">{tenant!.custom_domain}</span>
              </p>
            </div>
          )}

          <div className="mt-4 text-center">
            <p className="text-[#1A1A2E]/20 text-[10px]">
              Powered by{" "}
              <Link to="/" className="hover:underline" style={{ color: primary }}>
                Pixory AI
              </Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}